import { getApiErrorKind, getHttpStatus, prophecyErrorToError } from '@prophecy/utils/error';
import { captureException as sentryCaptureException, Scope, withScope, addBreadcrumb } from '@sentry/react';

import { NetworkConnection } from '../../utils/types';
import { GroupAs } from './groups';
import { SentryBreadcrumb } from './SentryBreadcrumb';
import { ErrorTagsType, SentryTags } from './SentryTags';

import type { ErrorEvent, EventHint } from '@sentry/react';

type Params = Parameters<typeof sentryCaptureException>;

/** Where the real message goes once `groupAs` has replaced it, with whatever the caller knew about
 * it. Contexts are not indexed, so the cardinality that made it unusable as a title costs nothing here. */
const ORIGINAL_ERROR_CONTEXT = 'original error';

/** Carries the `groupAs` request from the capture to `beforeSend`; removed again there. */
const GROUP_AS_CONTEXT = 'prophecy.groupAs';

/**
 * The backend cannot raise these, and `fetchURL` - which every REST and GraphQL client goes through -
 * wraps a network loss before it gets this far, so one arriving with a `groupAs` is the app's own bug,
 * caught by an error handler on its way out. It keeps its stack and its own issue instead of the group.
 */
const RUNTIME_ERRORS = [TypeError, ReferenceError, RangeError];
export const isCodeBug = (error: unknown) => RUNTIME_ERRORS.some((type) => error instanceof type);

/**
 * Applies a `groupAs` recorded by `captureException`: one fixed title and fingerprint, so a family of
 * failures stays one issue however much its messages vary.
 *
 * Deliberately here rather than on the capture's own scope. Sentry's Dedupe integration is an event
 * processor, and every processor runs before `beforeSend`; it drops an event whose title, fingerprint
 * and stack all match the one immediately before it - precisely the three this makes constant. Setting
 * them early therefore meant one call site failing in a burst reported once and the repeat count went
 * with it, and the count is what separates an outage from a blip. Measured against the dev project:
 * 5 captures differing only in the backend message underneath arrived as 2 events. Leaving the real
 * message in place until Dedupe has looked keeps them distinct, and costs nothing - a genuinely
 * identical repeat still dedupes, which is what that integration is for.
 */
export function applyGroupAs(event: ErrorEvent) {
  const groupAs = event.contexts?.[GROUP_AS_CONTEXT] as GroupAs | undefined;
  if (!groupAs) return event;

  delete event.contexts?.[GROUP_AS_CONTEXT];
  event.fingerprint = groupAs.fingerprint;
  // values[0] is what Sentry titles the issue from, and what titleUntitledRejection reads
  const exception = event.exception?.values?.[0];
  if (exception) exception.value = groupAs.title;
  return event;
}

export function captureException({
  exception: _exception,
  captureContext,
  errorTags,
  groupAs,
  original
}: {
  exception: Params[0];
  captureContext?: Params[1];
  errorTags?: ErrorTagsType;
  /** collapse this failure into one issue rather than one per message - see `GroupAs` */
  groupAs?: GroupAs;
  /** extra fields for the `original error` context, which `groupAs` otherwise reduces to name and message */
  original?: Record<string, unknown>;
}) {
  const exception = _exception as Error;
  const message: string = exception.message || '';
  // expected conditions (bad login, session expiry, transient network) opt out via ProphecyError.report
  if ((_exception as { report?: boolean })?.report === false) return;
  // Deliberately the original message, not the retitled one: the blacklist is what keeps backend
  // Spark and Java stack text out, and `groupAs` would otherwise smuggle all of it back in.
  if (message && !blacklistKeywords.some((word) => message.includes(word)))
    withScope(function (scope) {
      if (errorTags) {
        Object.entries(errorTags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }
      setNetworkConnectionTags(scope);
      // read before prophecyErrorToError, which flattens to a plain Error and drops them
      const status = getHttpStatus(_exception);
      if (status) scope.setTag(SentryTags.NetworkStatus, String(status));
      const kind = getApiErrorKind(_exception);
      if (kind) scope.setTag(SentryTags.ApiErrorKind, kind);
      const declined = groupAs && isCodeBug(_exception);
      if (groupAs && !declined) {
        // Only recorded here. `applyGroupAs` carries it out from beforeSend - see there for why the
        // title and the fingerprint must not be set on this scope.
        scope.setContext(GROUP_AS_CONTEXT, groupAs);
      } else if (groupAs) {
        // which bucket it was caught in, since the issue it files under will not say
        scope.setTag(SentryTags.GroupAsDeclined, groupAs.fingerprint.join('/'));
      }
      if (original || (groupAs && !declined)) {
        scope.setContext(ORIGINAL_ERROR_CONTEXT, { ...original, name: exception.name, message });
      }
      sentryCaptureException(prophecyErrorToError(exception), captureContext);
    });
}

// Sentry builds an issue title from the exception's name and message. Code that rejects a raw wire
// payload gives it neither, so it falls back to listing the object's keys and thousands of unrelated
// failures collapse into a handful of untitled issues. The payloads do carry a message — just under
// their own field name — so lift it into the exception value and the title comes back.
const UNTITLED_REJECTION = /^Object captured as promise rejection with keys:/;

const payloadMessage = (payload: unknown) => {
  const { message, msg } = (payload ?? {}) as { message?: unknown; msg?: unknown };
  const text = typeof message === 'string' ? message : typeof msg === 'string' ? msg : undefined;
  return text?.trim() || undefined;
};

export function titleUntitledRejection(event: ErrorEvent, hint?: EventHint) {
  const exception = event.exception?.values?.[0];
  if (!exception?.value || !UNTITLED_REJECTION.test(exception.value)) return event;

  const message = payloadMessage(hint?.originalException);
  if (!message) return event;

  // keep the key list, which is the only record of the shape that was rejected
  event.tags = { ...event.tags, [SentryTags.RejectedPayloadKeys]: exception.value };
  exception.value = message;
  return event;
}

// A chunk's URL carries its content hash, so every deploy shatters the same failing import into fresh
// issues and a recurring problem reads as a handful of one-off events. Drop the origin and the hash and
// one chunk stays one issue across builds.
//
// Every value, not just the first: a lazy route failing inside an ErrorBoundary arrives as a chained
// exception, and Sentry titles the issue from a different entry than the one `values[0]` points at - so
// rewriting only that one left the hash in the title and the per-deploy split in place.
const DYNAMIC_IMPORT_FAILURE = /(dynamically imported module|preload CSS for)\s*:?\s*(https?:\/\/\S+)/i;
const CHUNK_HASH = /\.[A-Za-z0-9_-]{8,}\.(js|css|mjs)$/;

export function normalizeChunkUrl(event: ErrorEvent) {
  event.exception?.values?.forEach((exception) => {
    const match = exception.value?.match(DYNAMIC_IMPORT_FAILURE);
    if (!exception.value || !match) return;

    try {
      const { pathname } = new URL(match[2]);
      exception.value = exception.value.replace(match[2], pathname.replace(CHUNK_HASH, '.$1'));
    } catch {
      // an unparseable URL is not worth losing the event over; leave the message as it came
    }
  });
  return event;
}

function setNetworkConnectionTags(scope: Scope) {
  scope.setTag(SentryTags.NetworkOnline, String(window.navigator.onLine));
  scope.setTag(SentryTags.PageVisibility, document.visibilityState);

  /** window.navigator.connection is not supported by all browsers, adding @ts-ignore to skip typing */

  const connection = (window.navigator as unknown as { connection: NetworkConnection }).connection;
  if (connection) {
    // Note: properties of window.navigator.connection is non-enumerable
    scope.setTag(SentryTags.NetworkConnectionDownlink, connection.downlink);
    scope.setTag(SentryTags.NetworkConnectionEffectiveType, connection.effectiveType);
    scope.setTag(SentryTags.NetworkConnectionRtt, connection.rtt);
    scope.setTag(SentryTags.NetworkConnectionSaveData, connection.saveData);
    scope.setTag(SentryTags.NetworkConnectionType, connection.type);
  }
}
export function listenForNetwork() {
  const connection = (window.navigator as unknown as { connection: NetworkConnection }).connection;
  if (connection) {
    connection.addEventListener('change', (event) => {
      const networkObject = event.currentTarget as unknown as NetworkConnection;
      let message = 'Network Connection changed';
      if (networkObject) {
        // Note: properties of window.navigator.connection is non-enumerable
        message = `${message} 
        ${JSON.stringify({
          downlink: networkObject.downlink,
          effectiveType: networkObject.effectiveType,
          rtt: networkObject.rtt,
          saveData: networkObject.saveData,
          type: networkObject.type
        })}`;
      }
      addBreadcrumb({
        category: SentryBreadcrumb.NetworkConnection,
        message: message,
        level: 'info',
        data: event.currentTarget as object
      });
    });
  }
  window.addEventListener('offline', (_e) => {
    addBreadcrumb({
      category: SentryBreadcrumb.NetworkConnection,
      message: 'Network is offline',
      level: 'info'
    });
  });

  window.addEventListener('online', (_e) => {
    addBreadcrumb({
      category: SentryBreadcrumb.NetworkConnection,
      message: 'Network is online',
      level: 'info'
    });
  });
}
// ~90% of prod Sentry error volume comes from our own headless automation suite; we skip init for these.
export function isAutomatedBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  return navigator.webdriver === true || /HeadlessChrome/i.test(navigator.userAgent);
}

// Session expired, already surfaced by the login redirect and not an app bug. 403s deliberately still
// report — those are real permission or credential misconfigurations worth seeing.
export const isExpectedAuthStatus = (status?: number) => status === 401;

// @monaco-editor/loader rejects with this exact object when useMonaco's cleanup cancels a load that
// then resolves anyway — an unmount race with nothing to fix. It carries no marker naming the package,
// so both fields are matched; the message is the loader's own literal.
const MONACO_LOADER_CANCELATION = { type: 'cancelation', msg: 'operation is manually canceled' };

export const isMonacoLoaderCancelation = (payload: unknown) => {
  const { type, msg } = (payload ?? {}) as { type?: unknown; msg?: unknown };
  return type === MONACO_LOADER_CANCELATION.type && msg === MONACO_LOADER_CANCELATION.msg;
};

// Benign third-party teardown races we never want reported (Slate editor + Monaco diff editor).
// Expected app conditions are suppressed via ProphecyError.report instead (see beforeSend), not by message.
export const sentryIgnoreErrors = [
  'Cannot resolve a DOM node from Slate node',
  'TextModel got disposed before DiffEditorWidget model got reset',
  // Qualified chat widget's Postmate rejects with this bare string when its ad-blocked iframe never replies
  'Handshake Timeout Reached'
];

const packages = [`java.io`, `java.nio`, `com.databricks.sql`, `org.apache`, 'java.lang.ClassNotFoundException'];
const words = [
  `NameError`,
  `AnalysisException`,
  `FileNotFoundError`,
  `Job aborted due to stage failure`,
  `ParseException`,
  `RecordFormatException`,
  `IndentationError`,
  `AttributeError`,
  'IllegalArgumentException',
  'FileAlreadyExistsException'
];
const blacklistKeywords = [...packages, ...words];
